import { Menu, MenuItem, MenuItemLabel } from '@/components/ui/menu';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { HStack } from '@/components/ui/hstack';
import { Box } from '@/components/ui/box';
import { Avatar, AvatarBadge } from '@/components/ui/avatar';
import i18n from 'i18n-js';
import { SafeAreaView } from 'react-native-safe-area-context';
import React, { useEffect } from 'react';
import { Linking, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import app from '../../app.json';
import { switchScreen } from '../../hooks/navigate';
import ModalWaiting from '../../screens/modals/ModalWaiting';
import { backendRequest } from "../../stores/thunks";
import ImageSynced from '../media/ImageSynced';
import * as MediaSelector from '../media/MediaSelector';
import { IconM, IconMC } from '../media/IconsV2';
import { updateSettings } from "../../stores/mainSlice";
import OpenIssueButton from "@/components/basic/OpenIssueButton";

export default function Header({ navigation, options }) {
    const taskid = options?.task?.workflowId || options?.task?.id
    const user = useSelector((state) => state.user)
    const perf = useSelector((state) => state.temp.perf)
    const server = useSelector((state) => state.server)
    const remote = useSelector((state) => state.remote)
    const settings = useSelector((state) => state.settings)
    const imgLoad = useSelector((state) => state.images.load)
    const notify = useSelector((state) => state.notify || {})
    const commit = useSelector((state) => state.commit || '-')

    const hidePhone = options.hidePhone === undefined ? false : options.hidePhone
    const showReport = options.showReport === undefined ? false : options.showReport && !user.demo
    const showMoreOptionsMenu = options.showMoreOptionsMenu === undefined ? false : options.showMoreOptionsMenu && !user.demo
    const canReport = showReport && taskid && taskid.length > 2 && !options?.task?.simulatorPreview

    // component mounted
    useEffect(() => {
        if (user.avatar) {
            MediaSelector.requestPermissions()
            MediaSelector.ensureDirExists()
        }
    }, [])

    return (
        <SafeAreaView edges={['top']} className="bg-primary-900">
            <VStack className={` relative bg-primary-900 px-3 pb-1 justify-between `}>
                <HStack space="1" className="items-start justify-between">
                    <HStack className="flex-1">
                        {options.homeScreen ? (
                            <VStack className="mr-2">
                                {user.avatar && (
                                    <Box className={` ${server.online ? (server.url.startsWith('https://app.') ? 'border-[#00FF00]' : 'border-[#FF00FF]') : 'border-white'} border-[2px] rounded-full p-2 `}>
                                        <Pressable onPress={() => navigation.openDrawer()}>
                                            <Avatar>
                                                <ImageSynced key={user.avatar + '_' + imgLoad[user.avatar]?.duration + '_' + user.updated} uri={user.avatar} refresh={user.updated} isAvatar={true} size='lg' className='border-primary-900 border bg-secondary-50' />
                                                <AvatarBadge className={`-bottom-2 -right-2 ${notify.header ? 'border-secondary-50' : 'border-transparent'} ${notify.header ? 'bg-mark-900' : 'bg-transparent'} border `}></AvatarBadge>
                                            </Avatar>
                                        </Pressable>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            !hidePhone &&
                            !settings.hidePhoneButton && (
                                <VStack>
                                    <Box className="border-[2px] border-white rounded-full mr-2 mt-1">
                                        {settings.preview ? 
                                        <OpenIssueButton navigation={navigation} icon='bug'/>
                                        :
                                        <Pressable
                                            onPress={() => {
                                                Linking.openURL(user.demo ? 'tel:+4999999999999' : 'tel:' + settings.contactTel);
                                            }}
                                            className="rounded-full border-2 border-primary-900 p-[1.5px] bg-secondary-50 self-end">
                                            <IconMC className="text-4xl text-primary-900" name="phone" />
                                        </Pressable>
                                        }
                                    </Box>
                                </VStack>
                            )
                        )}
                        <VStack className="flex-1 mr-3">
                            <Text className="text-2xl leading-xl text-secondary-50 mt-0">{options.title}</Text>
                            <Text className="text-lg leading-xl text-secondary-50 mt-0 line-clamp-1">
                                {options.info}
                            </Text>
                        </VStack>
                    </HStack>
                    {(canReport || showMoreOptionsMenu) && <MoreOptionsMenu navigation={navigation} options={options} showReport={canReport} info={options.info} />}
                    {options.homeScreen && !hidePhone && !settings.hidePhoneButton && (
                        <VStack>
                            <Box className={` ${!settings.hideVersion ? 'mr-4' : 'mr-0'} border-[2px] border-white bg-white rounded-full mt-1 `}>
                                {settings.preview ? 
                                <OpenIssueButton navigation={navigation} icon='bug' />
                                :
                                <Pressable
                                    onPress={() => {
                                        Linking.openURL(user.demo ? 'tel:+4999999999999' : 'tel:' + settings.contactTel);
                                    }}
                                    className="rounded-full border-[2px] border-primary-900 p-[1.5px] bg-secondary-50 self-end">
                                    <IconMC className="text-4xl text-primary-900" name="phone" />
                                </Pressable>
                                }
                            </Box>
                        </VStack>
                    )}
                </HStack>
                {Platform !== 'web' && remote.active && <IconM className="absolute right-1 -top-6 text-2xl color-[#00FF00]" name="file-download" />}
                {!settings.hideVersion && !options?.task?.simulatorPreview && (
                    <Text style={{ transform: [{ rotate: '-90deg' }] }} className={`top-2 ${settings.preview ? '-right-4' : '-right-1'} text-sm text-secondary-50 absolute `}>
                        {settings.preview ? commit : 'v' + app.expo.version }
                    </Text>
                )}
                {perf && <Text className={` absolute -top-4 right-8 text-sm leading-md text-white `}>{perf.screen + ' - ' + perf.info + ' ' + perf.delta + 'ms'}</Text>}
            </VStack>
            {Platform !== 'web' && <ModalWaiting showModal={/* remote.syncActive */ false} />}
        </SafeAreaView>
    );
}

function MoreOptionsMenu({ navigation, options, showReport, info }) {
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings);
    const server = useSelector((state) => state.server);

    const setPinned = () => {
        let pinned = settings.pinned ? [...settings.pinned] : [];
        if (pinned.includes(options.task.id)) {
            pinned.splice(pinned.indexOf(options.task.id), 1);
        } else {
            pinned.push(options.task.id);
        }
        dispatch(updateSettings({ settings: { pinned: pinned } }))
    }

    if (showReport) {
        if (options.owned) {
            return (
                <Box className={` ${!settings.hideVersion ? 'mr-3' : 'mr-0'} self-end `}>
                    <Menu
                        trigger={(triggerProps) => {
                            return (
                                <Pressable accessibilityLabel="More options menu" {...triggerProps}>
                                    <IconM className="text-3xl p-3 text-secondary-50" name="more-vert" />
                                </Pressable>
                            );
                        }}>
                        {server.online && (
                            <MenuItem onPress={() => switchScreen(navigation, 'ShareWorkflow', { task: options.task, info: info, isEdit: true })} textValue={i18n.t('moreOptionsMenu.share')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.share')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        {server.online && (
                            <MenuItem onPress={() => switchScreen(navigation, 'ShareQRCode', { task: options.task, isEdit: true })} textValue={i18n.t('moreOptionsMenu.publicshare')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.publicshare')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        {server.online && !options.task.studio && (
                            <MenuItem onPress={() => switchScreen(navigation, 'ShareWorkflow', { task: options.task, info: info, isEdit: true, isCopy: true })} textValue={i18n.t('moreOptionsMenu.copy')}>
                                <MenuItemLabel className='text-lg' >{i18n.t('moreOptionsMenu.copy')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        {server.online && (
                            <MenuItem onPress={() => switchScreen(navigation, 'Contacts', { isPlanner: true, isFinal: false, task: options.task })} textValue={i18n.t('moreOptionsMenu.plan')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.plan')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        {server.online && !options.task.studio && (
                            <MenuItem
                                onPress={() => {
                                    dispatch(backendRequest('SEND_WORKFLOW_INLINE', '/workflow/' + options.task.workflowId, null, 'POST', false)).then((result) => dispatch(backendRequest('GET_CONTACTS_INLINE', '/contact/user', null, 'GET', false)));
                                    switchScreen(navigation, 'BACK');
                                }}
                                textValue={i18n.t('moreOptionsMenu.duplicate')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.duplicate')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        <MenuItem onPress={setPinned} textValue={i18n.t(settings.pinned && settings.pinned.includes(options.task.id) ? 'moreOptionsMenu.unpin' : 'moreOptionsMenu.pin')}>
                            <MenuItemLabel className='text-lg'>{i18n.t(settings.pinned && settings.pinned.includes(options.task.id) ? 'moreOptionsMenu.unpin' : 'moreOptionsMenu.pin')}</MenuItemLabel>
                        </MenuItem>
                        {server.online && options.task.step == -1 && (
                            <MenuItem onPress={() => switchScreen(navigation, 'TaskSettings', { task: options.task })} textValue={i18n.t('moreOptionsMenu.settings')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.settings')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        <MenuItem onPress={() => switchScreen(navigation, 'ModalReport', { taskid: options.task.workflowId })} textValue={i18n.t('moreOptionsMenu.report')}>
                            <MenuItemLabel className='text-lg text-error-600'>
                                {i18n.t('moreOptionsMenu.report')}
                            </MenuItemLabel>
                        </MenuItem>
                    </Menu>
                </Box>
            );
        } else {
            return (
                <Box className={` ${!settings.hideVersion ? 'mr-3' : 'mr-0'} self-end `}>
                    <Menu
                        trigger={(triggerProps) => {
                            return (
                                <Pressable accessibilityLabel="More options menu" {...triggerProps}>
                                    <IconM className="text-3xl p-3 text-secondary-50" name="more-vert" />
                                </Pressable>
                            );
                        }}>
                        <MenuItem onPress={() => switchScreen(navigation, 'ScheduleWorkflow', { task: options.task, plans: [null] })} textValue={i18n.t('moreOptionsMenu.plan')}>
                            <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.plan')}</MenuItemLabel>
                        </MenuItem>
                        <MenuItem onPress={setPinned} textValue={i18n.t(settings.pinned && settings.pinned.includes(options.task.id) ? 'moreOptionsMenu.unpin' : 'moreOptionsMenu.pin')}>
                            <MenuItemLabel className='text-lg'>{i18n.t(settings.pinned && settings.pinned.includes(options.task.id) ? 'moreOptionsMenu.unpin' : 'moreOptionsMenu.pin')}</MenuItemLabel>
                        </MenuItem>
                        <MenuItem onPress={() => switchScreen(navigation, 'ModalReport', { taskid: options.task.workflowId })} textValue={i18n.t('moreOptionsMenu.report')}>
                            <MenuItemLabel className='text-lg text-error-600'>
                                {i18n.t('moreOptionsMenu.report')}
                            </MenuItemLabel>
                        </MenuItem>
                    </Menu>
                </Box>
            );
        }
    } else {
        if (options.public) {
            return (
                <Box className={` ${!settings.hideVersion ? 'mr-3' : 'mr-0'} self-end `}>
                    <Menu
                        trigger={(triggerProps) => {
                            return (
                                <Pressable accessibilityLabel="More options menu" {...triggerProps}>
                                    <IconM className="text-3xl p-3 text-secondary-50" name="more-vert" />
                                </Pressable>
                            );
                        }}>
                        <MenuItem onPress={() => switchScreen(navigation, 'accountSettings', { isEdit: false, isAnonymous: true })} textValue={i18n.t('accountSettings.gotoPublic')}>
                            <MenuItemLabel className='text-lg'>{i18n.t('accountSettings.gotoPublic')}</MenuItemLabel>
                        </MenuItem>
                    </Menu>
                </Box>
            );
        } else {
            return (
                <Box className={` ${!settings.hideVersion ? 'mr-3' : 'mr-0'} self-end `}>
                    <Menu
                        trigger={(triggerProps) => {
                            return (
                                <Pressable accessibilityLabel="More options menu" {...triggerProps}>
                                    <IconM className="text-3xl p-3 text-secondary-50" name="more-vert" />
                                </Pressable>
                            );
                        }}>
                        {server.online && (
                            <MenuItem className='active:bg-primary-900' onPress={() => dispatch(backendRequest('GET_WORKFLOWS_INLINE', '/contact/user', null, 'GET', false))} textValue={i18n.t('moreOptionsMenu.refresh')}>
                                <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.refresh')}</MenuItemLabel>
                            </MenuItem>
                        )}
                        <MenuItem onPress={() => switchScreen(navigation, 'DeletedWorkflows')} textValue={i18n.t('moreOptionsMenu.trash')}>
                            <MenuItemLabel className='text-lg'>{i18n.t('moreOptionsMenu.trash')}</MenuItemLabel>
                        </MenuItem>
                    </Menu>
                </Box>
            );
        }
    }
}
